/* eslint-disable @typescript-eslint/no-use-before-define */
import {
  addDays,
  addMonths,
  addWeeks,
  addYears,
  differenceInDays,
  differenceInMonths,
  differenceInWeeks,
  differenceInYears,
  isSameDay,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
} from 'date-fns';

import { ScheduleElementType } from '../components/MySchedule/types';
import { STATIC_ELEMENT_HEIGHT } from '../constants';
import { ScheduleElementColor } from '../graphql/components';
import { ONE_HOUR, MIN_15, MIN_15_DROP_ELEMENT_WIDTH } from '../scenes/Scheduling/config';
import { ChartScheduleElement } from '../scenes/Scheduling/types';

const DEFAULT_TICKS_SPACING = 15;
const MAX_PERCENTAGE_VALUE = 100;
const MAX_HEXADECIMAL_COLOR_VALUE = 255;

export const DEFAULT_OUTLINE_COLOR_OPACITY = 0.8;

export interface ScrollPosition {
  x: number;
  y: number;
}

export const timeAxisTicksScale = {
  year: 'year',
  month: 'month',
  week: 'week',
  day: 'day',
  default: 'default',
} as const;

export const defaultScrollPosition = { x: 0, y: 0 };

export const generateChartTicks = (min: number, max: number) => {
  let pointer = min;
  const ticks = [];
  const delta = max - min;

  if (!delta) {
    return [max];
  }

  const maxTickRange = delta && delta / 4;
  let spacing;

  if (maxTickRange >= DEFAULT_TICKS_SPACING) {
    spacing = Math.ceil(delta / 6 / 5) * 5;
  } else if (maxTickRange >= 10) {
    spacing = 10;
  } else if (maxTickRange >= 5) {
    spacing = 5;
  } else {
    spacing = Math.floor(maxTickRange);
  }

  while (pointer <= max) {
    ticks.push(pointer);
    pointer += spacing;
  }
  return ticks;
};

export const calculateChartBoundaries = (data: any, percentageBoundaryOverflow: number) => {
  if (!data) {
    return {};
  }
  const boundaries = { ...data };
  Object.keys(data).forEach((parameter: string) => {
    const { min: defaultMin, max: defaultMax } = boundaries[parameter];
    const min = defaultMin - Math.ceil(defaultMax * percentageBoundaryOverflow);
    const minAbs = min < 0 ? 0 : min - (min % 5);
    const max = defaultMax + Math.ceil(defaultMax * percentageBoundaryOverflow);
    const maxConstrained =
      parameter.match('Percentage') && max > MAX_PERCENTAGE_VALUE ? MAX_PERCENTAGE_VALUE : Math.ceil(max / 10) * 10;

    boundaries[parameter] = {
      min: minAbs,
      max: maxConstrained,
      ticks: generateChartTicks(minAbs, maxConstrained).reverse(),
    };
  });
  return boundaries;
};

export const generateTimeAxisTicks = (min: number, max: number, localeObj: Locale) => {
  let difference = differenceInYears(max, min);
  if (difference > 1) {
    return {
      type: timeAxisTicksScale.year,
      ticks: generateAnnualTimeAxisTicks(min, max, difference),
    };
  }

  difference = differenceInMonths(max, min);
  if (difference > 5) {
    return {
      type: timeAxisTicksScale.month,
      ticks: generateMonthlyTimeAxisTicks(min, max, difference),
    };
  }

  difference = differenceInWeeks(max, min);
  if (difference > 5) {
    return {
      type: timeAxisTicksScale.week,
      ticks: generateWeeklyTimeAxisTicks(min, max, difference, localeObj),
    };
  }

  difference = differenceInDays(max, min);
  if (difference) {
    return {
      type: timeAxisTicksScale.day,
      ticks: generateDailyTimeAxisTicks(min, max, difference),
    };
  }

  return {
    type: timeAxisTicksScale.default,
    ticks: [],
  };
};

export const generateAnnualTimeAxisTicks = (min: number, max: number, difference: number) => {
  const annualTicks = [];
  annualTicks.push(min);
  let currentDifference = difference;
  let currentYear = startOfYear(min);
  while (currentDifference) {
    currentYear = addYears(currentYear, 1);
    annualTicks.push(currentYear.getTime());
    currentDifference -= 1;
  }
  if (!isSameDay(annualTicks[annualTicks.length - 1], max)) {
    annualTicks.push(max);
  }
  return annualTicks;
};

export const generateMonthlyTimeAxisTicks = (min: number, max: number, difference: number) => {
  const monthlyTicks = [];
  monthlyTicks.push(min);
  let currentDifference = difference;
  let currentMonth = startOfMonth(min);
  while (currentDifference) {
    currentMonth = addMonths(currentMonth, 1);
    monthlyTicks.push(currentMonth.getTime());
    currentDifference -= 1;
  }
  if (!isSameDay(monthlyTicks[monthlyTicks.length - 1], max)) {
    monthlyTicks.push(max);
  }
  return monthlyTicks;
};

export const generateWeeklyTimeAxisTicks = (min: number, max: number, difference: number, localeObj: Locale) => {
  const weeklyTicks = [];
  weeklyTicks.push(min);
  let currentDifference = difference;
  let currentWeek = startOfWeek(min, { locale: localeObj });
  while (currentDifference) {
    currentWeek = addWeeks(currentWeek, 1);
    weeklyTicks.push(currentWeek.getTime());
    currentDifference -= 1;
  }
  if (!isSameDay(weeklyTicks[weeklyTicks.length - 1], max)) {
    weeklyTicks.push(max);
  }
  return weeklyTicks;
};

export const generateDailyTimeAxisTicks = (min: number, max: number, difference: number) => {
  const dailyTicks = [];
  dailyTicks.push(min);
  let currentDifference = difference;
  let currentDay = startOfDay(min);
  while (currentDifference) {
    currentDay = addDays(currentDay, 1);
    dailyTicks.push(currentDay.getTime());
    currentDifference -= 1;
  }
  if (!isSameDay(dailyTicks[dailyTicks.length - 1], max)) {
    dailyTicks.push(max);
  }
  return dailyTicks;
};

export const transformColorRGBAToObj = (color: string) => {
  const parsedColors = color.replace(/[^\d,]/g, '').split(',');

  return {
    red: Number.parseInt(parsedColors?.[0], 10),
    green: Number.parseInt(parsedColors?.[1], 10),
    blue: Number.parseInt(parsedColors?.[2], 10),
  } as ScheduleElementColor;
};

/**
 * converts color to the lighter/darker one
 * @param color original color (rgb)
 * @param percentageChange number in range of -100 and 100 allowing to use negative opacity
 */
export const shadeColor = (color: ScheduleElementColor, percentageChange: number) => {
  let red = Math.ceil(color.red * ((100 + percentageChange) / 100));
  let green = Math.ceil(color.green * ((100 + percentageChange) / 100));
  let blue = Math.ceil(color.blue * ((100 + percentageChange) / 100));

  if (red > MAX_HEXADECIMAL_COLOR_VALUE) {
    red = MAX_HEXADECIMAL_COLOR_VALUE;
  }

  if (blue > MAX_HEXADECIMAL_COLOR_VALUE) {
    blue = MAX_HEXADECIMAL_COLOR_VALUE;
  }

  if (green > MAX_HEXADECIMAL_COLOR_VALUE) {
    green = MAX_HEXADECIMAL_COLOR_VALUE;
  }

  return {
    red,
    blue,
    green,
  };
};

export const formatScheduleElementRGBAColor = (color: ScheduleElementColor, alpha = 1) =>
  `rgba(${color.red}, ${color.green}, ${color.blue}, ${alpha})`;

export const getScrollPosition = (
  scheduleElements: Array<ScheduleElementType | ChartScheduleElement>,
  selectedDate: number
): ScrollPosition => {
  if (!scheduleElements.length) {
    return defaultScrollPosition;
  }

  const { start, y = 0 } = scheduleElements.reduce((prev, curr) =>
    prev.start < curr.start ||
    (prev.start === curr.start && curr.y !== undefined && prev.y !== undefined && prev.y < curr.y)
      ? prev
      : curr
  );
  const difference = Math.abs(start - selectedDate) - ONE_HOUR;
  const xPosition = (difference / MIN_15) * MIN_15_DROP_ELEMENT_WIDTH;

  const yPosition = y > 0 ? y * STATIC_ELEMENT_HEIGHT + 1 : 0;

  return {
    x: Math.max(0, xPosition),
    y: yPosition,
  };
};

/**
 * @source https://github.com/highcharts/highcharts/blob/master/js/parts/SvgRenderer.js#L3161
 */
export const getContrastColor = ({ red, green, blue }: ScheduleElementColor): ScheduleElementColor =>
  red + green * 1.2 + blue * 0.5 > 1.8 * MAX_HEXADECIMAL_COLOR_VALUE
    ? { red: 0, green: 0, blue: 0 }
    : { red: MAX_HEXADECIMAL_COLOR_VALUE, green: MAX_HEXADECIMAL_COLOR_VALUE, blue: MAX_HEXADECIMAL_COLOR_VALUE };

export const getTextShadowOfScheduleElement = (contrastColor: ScheduleElementColor) => {
  const outlineColor = formatScheduleElementRGBAColor(getContrastColor(contrastColor), DEFAULT_OUTLINE_COLOR_OPACITY);

  return `-1px -1px 0 ${outlineColor}, 1px -1px 0 ${outlineColor},
    -1px 1px 0 ${outlineColor}, 1px 1px 0 ${outlineColor},
    0 1px 0 ${outlineColor}, 0 -1px 0 ${outlineColor},
    1px 0 0 ${outlineColor}, -1px 0 0 ${outlineColor}`;
};
